import { useCallback, useEffect, useMemo, useRef } from 'react';
import { CollapsibleSection, LayerPaths, buildLayerUrl, useDirectionsController, useMapStore } from '@uofu-uss-gis/ui-react';
import { useTheme, alpha } from '@mui/material/styles';
import * as route from '@arcgis/core/rest/route';
import * as projectOperator from '@arcgis/core/geometry/operators/projectOperator';
import RouteParameters from '@arcgis/core/rest/support/RouteParameters';
import FeatureSet from '@arcgis/core/rest/support/FeatureSet';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import { classMapDirectionsController } from '../../services/directionsController';

const ROUTE_LAYER_ID = 'custom-directions-route-layer';

const ClassMapDirectionsSection = () => {
  const theme = useTheme();
  const { controller, state } = useDirectionsController(classMapDirectionsController);
  const { mapView, map, restEndpoint } = useMapStore();
  const structuresLayerRef = useRef<FeatureLayer | null>(null);
  const routeGraphicsLayerRef = useRef<GraphicsLayer | null>(null);
  const lastRawRouteResultRef = useRef<any>(null);

  useEffect(() => {
    if (!map) {
      return;
    }

    const existingLayer = map.findLayerById(ROUTE_LAYER_ID) as GraphicsLayer | undefined;
    routeGraphicsLayerRef.current = existingLayer ?? new GraphicsLayer({ id: ROUTE_LAYER_ID });

    if (!existingLayer) {
      map.add(routeGraphicsLayerRef.current);
    }

    return () => {
      if (routeGraphicsLayerRef.current) {
        map.remove(routeGraphicsLayerRef.current);
        routeGraphicsLayerRef.current.destroy();
        routeGraphicsLayerRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    return () => {
      structuresLayerRef.current?.destroy();
      structuresLayerRef.current = null;
    };
  }, []);

  const featureProvider = useMemo(() => ({
    queryFeatures: async (params: any) => {
      if (!structuresLayerRef.current) {
        structuresLayerRef.current = new FeatureLayer({
          url: buildLayerUrl(restEndpoint, LayerPaths.structures),
          outFields: ['OBJECTID', 'formal_name', 'informal_name', 'abbreviation', 'building_number']
        });
      }

      const result = await structuresLayerRef.current.queryFeatures({
        where: params.where,
        returnGeometry: params.returnGeometry,
        outFields: params.outFields,
        orderByFields: params.orderByFields,
        num: params.num
      });

      return { features: result.features ?? [] };
    }
  }), [restEndpoint]);

  const routeProvider = useMemo(() => ({
    solve: async (origin: any, destination: any) => {
      if (!mapView || !restEndpoint) {
        return null;
      }

      if (!projectOperator.isLoaded()) {
        await projectOperator.load();
      }

      const originGeometry = projectOperator.execute(origin, mapView.spatialReference) ?? origin;
      const destinationGeometry = projectOperator.execute(destination, mapView.spatialReference) ?? destination;
      const routeUrl = `https://${restEndpoint}/arcgis/rest/services/mapservices/path_network/NAServer/Route`;
      const routeResult = await route.solve(routeUrl, new RouteParameters({
        stops: new FeatureSet({
          features: [
            new Graphic({ geometry: originGeometry }),
            new Graphic({ geometry: destinationGeometry })
          ]
        }),
        returnRoutes: true,
        returnDirections: true,
        returnStops: true,
        findBestSequence: false,
        preserveFirstStop: true,
        preserveLastStop: true,
        ignoreInvalidLocations: false,
        outSpatialReference: mapView.spatialReference
      }));
      const firstResult = routeResult.routeResults?.[0];
      lastRawRouteResultRef.current = firstResult ?? null;

      const resolvedRoute = firstResult?.route;
      if (!resolvedRoute) {
        return { route: null };
      }

      return {
        route: {
          geometry: resolvedRoute.geometry,
          clone: () => resolvedRoute.clone()
        },
        directions: firstResult.directions ? {
          totalLength: firstResult.directions.totalLength ?? undefined,
          totalTime: firstResult.directions.totalTime ?? undefined,
          features: firstResult.directions.features
        } : undefined
      };
    }
  }), [mapView, restEndpoint]);

  const handleFindRoute = useCallback(async () => {
    if (!mapView || !restEndpoint || !routeGraphicsLayerRef.current) {
      controller.setErrorMessage('Map is still loading. Please try again in a moment.');
      return;
    }

    lastRawRouteResultRef.current = null;
    await controller.findRoute(featureProvider, routeProvider);

    const rawResult = lastRawRouteResultRef.current;
    if (!rawResult?.route) {
      routeGraphicsLayerRef.current.removeAll();
      return;
    }

    const controllerState = controller.getState();
    const routeGraphic = rawResult.route.clone();
    routeGraphic.symbol = new SimpleLineSymbol({
      color: theme.palette.primary.main,
      width: 4
    });

    const originMarker = new Graphic({
      geometry: controllerState.fromSelection?.point,
      symbol: new SimpleMarkerSymbol({
        color: theme.palette.text.primary,
        size: 10,
        outline: {
          color: theme.palette.background.paper,
          width: 1.5
        }
      })
    });

    const destinationMarker = new Graphic({
      geometry: controllerState.toSelection?.point,
      symbol: new SimpleMarkerSymbol({
        color: theme.palette.primary.main,
        size: 10,
        outline: {
          color: theme.palette.background.paper,
          width: 1.5
        }
      })
    });

    routeGraphicsLayerRef.current.removeAll();
    routeGraphicsLayerRef.current.addMany([routeGraphic, originMarker, destinationMarker]);

    if (routeGraphic.geometry) {
      await mapView.goTo(routeGraphic.geometry).catch(() => undefined);
    }
  }, [controller, featureProvider, mapView, restEndpoint, routeProvider, theme]);

  useEffect(() => {
    if (!state.fromSelection || !state.toSelection) {
      return;
    }

    void handleFindRoute();
  }, [state.fromSelection, state.toSelection, handleFindRoute]);

  const onInputChange = (field: 'from' | 'to', value: string) => {
    controller.setErrorMessage('');
    controller.setActiveInput(field);

    if (field === 'from') {
      controller.setFromQuery(value);
      controller.clearFromSelection();
    } else {
      controller.setToQuery(value);
      controller.clearToSelection();
    }

    void controller.fetchSuggestions(value, featureProvider);
  };

  const clearRoute = () => {
    controller.clear();
    routeGraphicsLayerRef.current?.removeAll();
  };

  const selectSuggestion = (field: 'from' | 'to', suggestion: any) => {
    controller.selectSuggestion(field, suggestion);
  };

  const openDefaultMaps = () => {
    const url = controller.getExternalMapsUrl();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <CollapsibleSection title="Directions" icon="/app/resources/images/icons/mockup/directions.png">
      <div className="w-full space-y-3" role="region" aria-label="Custom directions">
      <div className="space-y-2">
        <label htmlFor="directions-from" className="block text-xs font-semibold uppercase" style={{ color: alpha(theme.palette.text.primary, 0.75) }}>From</label>
        <input
          id="directions-from"
          type="text"
          value={state.fromQuery}
          onChange={(event) => onInputChange('from', event.target.value)}
          onFocus={() => {
            controller.setActiveInput('from');
            void controller.fetchSuggestions(state.fromQuery, featureProvider);
          }}
          placeholder="Building name, code, or number"
          className="w-full rounded border px-3 py-2 text-sm focus:outline-none"
          style={{ borderColor: alpha(theme.palette.text.primary, 0.25), backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary }}
          autoComplete="off"
        />
      </div>
      {state.activeInput === 'from' && state.suggestions.length > 0 && (
        <ul className="max-h-40 overflow-y-auto rounded border py-1" style={{ borderColor: alpha(theme.palette.text.primary, 0.15), backgroundColor: theme.palette.background.paper }}>
          {state.suggestions.map((suggestion: any) => (
            <li key={`from-${suggestion.id}`}>
              <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => selectSuggestion('from', suggestion)} className="w-full px-3 py-2 text-left text-sm" style={{ color: theme.palette.text.primary }}>
                {suggestion.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="space-y-2">
        <label htmlFor="directions-to" className="block text-xs font-semibold uppercase" style={{ color: alpha(theme.palette.text.primary, 0.75) }}>To</label>
        <input
          id="directions-to"
          type="text"
          value={state.toQuery}
          onChange={(event) => onInputChange('to', event.target.value)}
          onFocus={() => {
            controller.setActiveInput('to');
            void controller.fetchSuggestions(state.toQuery, featureProvider);
          }}
          placeholder="Building name, code, or number"
          className="w-full rounded border px-3 py-2 text-sm focus:outline-none"
          style={{ borderColor: alpha(theme.palette.text.primary, 0.25), backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary }}
          autoComplete="off"
        />
      </div>
      {state.activeInput === 'to' && state.suggestions.length > 0 && (
        <ul className="max-h-40 overflow-y-auto rounded border py-1" style={{ borderColor: alpha(theme.palette.text.primary, 0.15), backgroundColor: theme.palette.background.paper }}>
          {state.suggestions.map((suggestion: any) => (
            <li key={`to-${suggestion.id}`}>
              <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => selectSuggestion('to', suggestion)} className="w-full px-3 py-2 text-left text-sm" style={{ color: theme.palette.text.primary }}>
                {suggestion.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2 pt-1">
        <button type="button" onClick={() => { void handleFindRoute(); }} disabled={state.isSolvingRoute} className="rounded px-3 py-2 text-xs font-semibold uppercase disabled:cursor-not-allowed" style={{ backgroundColor: state.isSolvingRoute ? alpha(theme.palette.primary.main, 0.5) : theme.palette.primary.main, color: theme.palette.primary.contrastText }}>
          {state.isSolvingRoute ? 'Finding route...' : 'Get Directions'}
        </button>
        <button type="button" onClick={clearRoute} className="rounded border px-3 py-2 text-xs font-semibold uppercase" style={{ borderColor: theme.palette.primary.main, color: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.08) }}>
          Clear
        </button>
      </div>
      <button type="button" onClick={openDefaultMaps} disabled={!state.fromSelection || !state.toSelection} className="rounded border px-3 py-2 text-xs font-semibold uppercase disabled:cursor-not-allowed" style={{ borderColor: theme.palette.primary.main, color: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.08) }}>
        Open in Maps
      </button>
      {state.errorMessage && <div className="rounded px-3 py-2 text-xs" style={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`, backgroundColor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }}>{state.errorMessage}</div>}
      {state.summaryText && <div className="rounded px-3 py-2 text-xs font-medium" style={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`, backgroundColor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }}>{state.summaryText}</div>}
      {state.directionSteps.length > 0 && (
        <ol className="space-y-2 text-sm" style={{ color: theme.palette.text.primary }}>
          {state.directionSteps.map((step, index) => (
            <li key={`${step.text}-${index}`} className="border-b pb-2 last:border-b-0 last:pb-0" style={{ borderColor: alpha(theme.palette.text.primary, 0.15) }}>
              <div>{step.text}</div>
              {(step.length > 0 || step.time > 0) && <div className="text-xs" style={{ color: alpha(theme.palette.text.primary, 0.7) }}>{step.length.toFixed(2)} mi • {Math.max(1, Math.round(step.time))} min</div>}
            </li>
          ))}
        </ol>
      )}
      </div>
    </CollapsibleSection>
  );
};

export default ClassMapDirectionsSection;
